import { EFFECTIVE_ACCESS_PAGE_KEY } from "common/Constants/EffectiveAccess.constants";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  isPlatformAdminProfile,
  platformAdminUsesCustomerContextFromMe,
} from "common/Utilities/Role.utils";
import {
  ADMIN_SIDE_NAV_ITEMS,
  PLATFORM_SIDE_NAV_ITEMS,
  SIDE_NAV_PATH_ALIASES,
} from "./SideNav.constants";

/** Router location.state key for Data Management panel (not a URL query). */
export const DATA_MANAGEMENT_STATE_KEY = "dataManagementSub";
export const DATA_MANAGEMENT_DEFAULT_SUB = "edgeHierarchy";

export const DATA_MANAGEMENT_PATH_BY_SUB = {
  edgeHierarchy: INFRAMARK_CONSTANTS.navigationRoutes.edge,
  cloudHierarchy: INFRAMARK_CONSTANTS.navigationRoutes.cloud,
  tags: INFRAMARK_CONSTANTS.navigationRoutes.tags,
  imports: INFRAMARK_CONSTANTS.navigationRoutes.import,
};

const DATA_MANAGEMENT_SUB_BY_PATH = Object.entries(
  DATA_MANAGEMENT_PATH_BY_SUB
).reduce((acc, [subKey, path]) => {
  acc[path] = subKey;
  return acc;
}, {});

export const getDataManagementSubKeyFromPath = (pathname = "") => {
  const normalized = normalizeSideNavPath(pathname);
  return (
    DATA_MANAGEMENT_SUB_BY_PATH[normalized] ?? DATA_MANAGEMENT_DEFAULT_SUB
  );
};

const PAGE_KEY_TO_LEGACY_ACCESS = {
  [EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER]: "groups",
  [EFFECTIVE_ACCESS_PAGE_KEY.CONFIGURATIONS]: "thingtype",
  [EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL]: "platformuseraccess",
  [EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT]: "admin",
  [EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT]: "datamanagement",
};

const normalizeAccessKey = (key) => String(key ?? "").toLowerCase();

const legacyCanAccessPage = (pageKey, accessTo = []) => {
  if (!pageKey) {
    return true;
  }
  const normalizedAccess = accessTo.map(normalizeAccessKey);
  if (normalizedAccess.includes("all")) {
    return true;
  }
  const legacyKey = PAGE_KEY_TO_LEGACY_ACCESS[pageKey];
  if (!legacyKey) {
    return false;
  }
  return normalizedAccess.includes(legacyKey);
};

const canAccessNavPage = ({
  pageKey,
  usesEffectiveAccess,
  canAccessPage,
  accessTo,
}) => {
  if (!pageKey) {
    return true;
  }
  if (usesEffectiveAccess) {
    return canAccessPage(pageKey);
  }
  return legacyCanAccessPage(pageKey, accessTo);
};

const filterNavItem = (item, accessOptions) => {
  if (!canAccessNavPage({ pageKey: item.pageKey, ...accessOptions })) {
    return null;
  }

  const subPages = (item.subPages ?? [])
    .map((sub) => {
      if (
        !canAccessNavPage({
          pageKey: sub.pageKey ?? item.pageKey,
          ...accessOptions,
        })
      ) {
        return null;
      }
      return sub;
    })
    .filter(Boolean);

  if ((item.subPages ?? []).length > 0 && subPages.length === 0 && !item.path) {
    return null;
  }

  return {
    ...item,
    subPages,
  };
};

export const getVisibleSideNavItems = ({
  profile = {},
  usesEffectiveAccess = false,
  canAccessPage = () => true,
  accessTo = [],
} = {}) => {
  const isPlatformAdmin = isPlatformAdminProfile(profile);
  const inCustomerContext =
    platformAdminUsesCustomerContextFromMe(profile) || !isPlatformAdmin;

  const candidates = inCustomerContext
    ? ADMIN_SIDE_NAV_ITEMS
    : PLATFORM_SIDE_NAV_ITEMS;

  const accessOptions = { usesEffectiveAccess, canAccessPage, accessTo };

  return candidates
    .map((item) => filterNavItem(item, accessOptions))
    .filter(Boolean);
};

export const normalizeSideNavPath = (pathname = "") =>
  SIDE_NAV_PATH_ALIASES[pathname] ?? pathname;

const navPathname = (navPath = "") =>
  normalizeSideNavPath(String(navPath).split("?")[0]);

const pathMatches = (navPath, pathname) => {
  if (!navPath) {
    return false;
  }
  return navPathname(navPath) === normalizeSideNavPath(pathname);
};

const itemMatchesPath = (item, pathname) => {
  const candidates = [item.path, ...(item.matchPaths ?? [])].filter(Boolean);
  return candidates.some((candidate) => pathMatches(candidate, pathname));
};

export const isHomeLandingPath = (pathname) => {
  const normalized = normalizeSideNavPath(pathname);
  return normalized === "/" || normalized === "/home";
};

export const getNavNavigateOptions = () => undefined;

export const findSideNavSelection = (items = [], pathname = "") => {
  const normalizedPath = normalizeSideNavPath(pathname);

  for (const item of items) {
    const matchingSubs = (item.subPages ?? []).filter((sub) =>
      pathMatches(sub.path, normalizedPath)
    );
    if (matchingSubs.length === 0) {
      continue;
    }
    return {
      selectedId: item.id,
      selectedSubId: matchingSubs[0].id,
    };
  }

  for (const item of items) {
    if (itemMatchesPath(item, normalizedPath)) {
      return {
        selectedId: item.id,
        selectedSubId: null,
      };
    }
  }

  return {
    selectedId: items[0]?.id ?? null,
    selectedSubId: null,
  };
};

export const getDefaultSideNavPath = (items = []) => {
  const first = items[0];
  if (!first) {
    return "/access_denied";
  }
  if (first.path) {
    return navPathname(first.path);
  }
  const firstSubWithPath = (first.subPages ?? []).find((sub) => sub.path);
  return firstSubWithPath ? navPathname(firstSubWithPath.path) : "/access_denied";
};

export const getNavItemNavigatePath = (item) => {
  if (!item) {
    return null;
  }
  if (item.path) {
    return navPathname(item.path);
  }
  const firstSubWithPath = (item.subPages ?? []).find((sub) => sub.path);
  return firstSubWithPath ? navPathname(firstSubWithPath.path) : null;
};
